import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Divider,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as LocalOfferIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Tag } from '../../types';
import tagService from '../../services/tagService';
import { usePermissions } from '../../contexts/AuthContext';
import LinkedTagItemsDialog from './componentes/LinkedTagItemsDialog';

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const TagsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { can } = usePermissions();
  const podeCriarTag = can('atendimento.tags.criar');
  const podeEditarTag = can('atendimento.tags.editar');
  const podeDeletarTag = can('atendimento.tags.deletar');

  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [busca, setBusca] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTagId, setCurrentTagId] = useState<number | null>(null);

  // Estados do Modal de Vínculos
  const [linkedModalOpen, setLinkedModalOpen] = useState(false);
  const [linkedTag, setLinkedTag] = useState<Tag | null>(null);
  const [linkedType, setLinkedType] = useState<'contatos' | 'tickets'>('contatos');

  // Dados do formulário
  const [formData, setFormData] = useState({
    titulo: '',
    color: '#1565c0',
    ativo: true,
    compartilhada: true,
  });

  const handleOpenLinkedModal = (tag: Tag, type: 'contatos' | 'tickets') => {
    setLinkedTag(tag);
    setLinkedType(type);
    setLinkedModalOpen(true);
  };


  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tagService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: busca,
      });
      if (response.success && response.data) {
        setTags(response.data);
        if (response.pagination) {
          setTotalItems(response.pagination.total);
        }
      }
    } catch (error) {
      enqueueSnackbar('Erro ao carregar tags', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, busca, enqueueSnackbar]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchTags]);

  const handleOpenModal = (tag?: Tag) => {
    if (tag) {
      setIsEditing(true);
      setCurrentTagId(tag.id);
      setFormData({
        titulo: tag.titulo,
        color: tag.color,
        ativo: tag.ativo,
        compartilhada: tag.compartilhada !== false,
      });
    } else {
      setIsEditing(false);
      setCurrentTagId(null);
      setFormData({
        titulo: '',
        color: '#1565c0',
        ativo: true,
        compartilhada: true,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      enqueueSnackbar('O título da tag é obrigatório', { variant: 'warning' });
      return;
    }

    try {
      if (isEditing && currentTagId) {
        await tagService.update(currentTagId, formData);
        enqueueSnackbar('Tag atualizada com sucesso!', { variant: 'success' });
      } else {
        await tagService.create(formData);
        enqueueSnackbar('Tag criada com sucesso!', { variant: 'success' });
      }
      handleCloseModal();
      fetchTags();
    } catch (error) {
      enqueueSnackbar('Erro ao salvar tag', { variant: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta tag?')) {
      try {
        await tagService.delete(id);
        enqueueSnackbar('Tag excluída com sucesso!', { variant: 'success' });
        fetchTags();
      } catch (error) {
        enqueueSnackbar('Erro ao excluir tag', { variant: 'error' });
      }
    }
  };

  const handleToggleStatus = async (tag: Tag) => {
    try {
      await tagService.update(tag.id, { ativo: !tag.ativo });
      enqueueSnackbar(`Tag ${!tag.ativo ? 'ativada' : 'desativada'}!`, { variant: 'success' });
      fetchTags();
    } catch (error) {
      enqueueSnackbar('Erro ao alterar status', { variant: 'error' });
    }
  };

  const totalAtivas = tags.filter((t) => t.ativo).length;

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#f3e5f5', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <LocalOfferIcon sx={{ fontSize: 120, color: theme.palette.secondary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Tags de Atendimento</Typography>
              <Typography variant="caption" color="text.secondary">
                Gerencie as etiquetas usadas para classificar atendimentos e contatos
              </Typography>
            </Box>
          </Stack>
          {podeCriarTag && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenModal()} size="small">
              Nova Tag
            </Button>
          )}
        </Stack>
      </Paper>

      {!loading && totalItems > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Total de Tags" valor={totalItems} icone={<LocalOfferIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Ativas" valor={totalAtivas} subtitulo={`${totalItems ? Math.round((totalAtivas / totalItems) * 100) : 0}% do total`} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Inativas" valor={totalItems - totalAtivas} icone={<CancelIcon sx={{ color: theme.palette.error.main }} />} cor={theme.palette.error.main} bgCor={alpha(theme.palette.error.main, 0.12)} />
          </Grid>
        </Grid>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por título..."
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Listagem de Tags</Typography>
          {!loading && (
            <Chip size="small" label={`${totalItems} tag${totalItems !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando tags...</Typography>
          </Box>
        ) : tags.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <LocalOfferIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhuma tag encontrada</Typography>
            <Typography variant="caption" color="text.secondary">Crie uma nova tag ou ajuste a busca.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Cor</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell align="center">Visibilidade</TableCell>
                    <TableCell align="center">Contatos</TableCell>
                    <TableCell align="center">Tickets</TableCell>
                    <TableCell align="center">Ativo</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
              {tags.map((tag) => (
                <TableRow key={tag.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 } }}>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{tag.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: 24, height: 24, bgcolor: tag.color, borderRadius: 1, border: `1px solid ${alpha(tag.color, 0.4)}`, boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.5)}` }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={tag.titulo} size="small" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: alpha(tag.color, 0.15), color: tag.color, border: `1px solid ${alpha(tag.color, 0.3)}` }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={tag.compartilhada !== false ? 'Compartilhada' : 'Privada'}
                      color={tag.compartilhada !== false ? 'success' : 'default'}
                      variant={tag.compartilhada !== false ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography 
                      variant="caption" 
                      fontWeight={700}
                      sx={{ 
                        cursor: (tag.contatos_count || 0) > 0 ? 'pointer' : 'default',
                        color: (tag.contatos_count || 0) > 0 ? 'primary.main' : 'text.secondary',
                        textDecoration: (tag.contatos_count || 0) > 0 ? 'underline' : 'none',
                        '&:hover': {
                           color: (tag.contatos_count || 0) > 0 ? 'primary.dark' : 'text.secondary'
                        }
                      }}
                      onClick={() => (tag.contatos_count || 0) > 0 && handleOpenLinkedModal(tag, 'contatos')}
                    >
                      {tag.contatos_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography 
                      variant="caption" 
                      fontWeight={700}
                      sx={{ 
                        cursor: (tag.tickets_count || 0) > 0 ? 'pointer' : 'default',
                        color: (tag.tickets_count || 0) > 0 ? 'primary.main' : 'text.secondary',
                        textDecoration: (tag.tickets_count || 0) > 0 ? 'underline' : 'none',
                        '&:hover': {
                           color: (tag.tickets_count || 0) > 0 ? 'primary.dark' : 'text.secondary'
                        }
                      }}
                      onClick={() => (tag.tickets_count || 0) > 0 && handleOpenLinkedModal(tag, 'tickets')}
                    >
                      {tag.tickets_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={tag.ativo}
                      onChange={() => handleToggleStatus(tag)}
                      color="primary"
                      size="small"
                      disabled={!podeEditarTag}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {podeEditarTag && (
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenModal(tag)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {podeDeletarTag && (
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => handleDelete(tag.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              size="small"
              count={totalItems}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas:"
              sx={{
                minHeight: 44,
                '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 },
              }}
            />
          </>
        )}
      </Paper>

      {/* Modal Form */}
      <Dialog open={openModal && (isEditing ? podeEditarTag : podeCriarTag)} onClose={handleCloseModal} maxWidth="xs" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Tag' : 'Nova Tag'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Título"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
              />
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  label="Cor (Hexadecimal)"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', padding: 0 }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.compartilhada}
                    onChange={(e) => setFormData((prev) => ({ ...prev, compartilhada: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>Compartilhada</strong> (visível para todos os usuários)
                  </Typography>
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {isEditing ? 'Salvar Alterações' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <LinkedTagItemsDialog
        open={linkedModalOpen}
        onClose={() => setLinkedModalOpen(false)}
        tag={linkedTag}
        type={linkedType}
      />
    </Box>
  );
};

export default TagsPage;
